import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Message } from '../../interfaces/chat';
import ChatHeader from './components/ChatHeader/ChatHeader';
import ChatMessages from './components/ChatMessages/ChatMessages';
import ChatInput from './components/ChatInput/ChatInput';
import ChatFeedback from './components/ChatFeedback/ChatFeedback';
import styles from './chatWidget.module.scss';

const MIN_ASSISTANT_MESSAGES_FOR_FEEDBACK = 6;

interface ChatWidgetProps {
    isOpen: boolean;
    messages: Message[];
    isTyping: boolean;
    onClose: () => void;
    onRefresh: () => void;
    onSendMessage: (message: string) => void;
    onSendHiddenContext: (contextMessage: string, caseId: string) => void;
    onSendFeedback: (caseId: string, liked: boolean) => void;
    disabled: boolean;
    hasError: boolean;
    user?: any;
    caseId: string;
    feedbackSent: boolean;
    feedbackLiked: boolean | null;
}

const extractPublicationId = (pathname: string): string | null => {
    const directMatch = pathname.match(/\/(?:publication|publicacion)\/([a-fA-F0-9]{24})/);
    if (directMatch) return directMatch[1];

    const autosMatch = pathname.match(/\/autos\/(?:usados|nuevos)\/(?:.*\/)?([a-fA-F0-9]{24})/);
    if (autosMatch) return autosMatch[1];

    return null;
};

const ChatWidget: React.FC<ChatWidgetProps> = ({
    isOpen,
    messages,
    isTyping,
    onClose,
    onRefresh,
    onSendMessage,
    onSendHiddenContext,
    onSendFeedback,
    disabled,
    hasError,
    user,
    caseId,
    feedbackSent,
    feedbackLiked,
}) => {
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const [isClosing, setIsClosing] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);
    const [isSendingContext, setIsSendingContext] = useState(false);
    const [showConfirmRefresh, setShowConfirmRefresh] = useState(false);
    const lastSentPublicationIdRef = useRef<string | null>(null);
    const location = useLocation();

    const assistantMessageCount = useMemo(
        () => messages.filter(m => m.role === 'assistant' && !m.hidden).length,
        [messages]
    );
    const showFeedback = assistantMessageCount >= MIN_ASSISTANT_MESSAGES_FOR_FEEDBACK;

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsClosing(false);
        } else if (shouldRender) {
            setIsClosing(true);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsClosing(false);
            }, 250);
            return () => clearTimeout(timer);
        }
    }, [isOpen, shouldRender]);

    useEffect(() => {
        if (!isOpen) return;

        const publicationId = extractPublicationId(location.pathname);
        if (publicationId && publicationId !== lastSentPublicationIdRef.current) {
            lastSentPublicationIdRef.current = publicationId;
            setIsSendingContext(true);
            Promise.resolve(onSendHiddenContext(`publication/${publicationId}`, caseId))
                .finally(() => setIsSendingContext(false));
        }
    }, [isOpen, location.pathname, onSendHiddenContext, caseId]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (chatContainerRef.current && !chatContainerRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleRefreshClick = () => {
        setShowConfirmRefresh(true);
    };

    const handleConfirmRefresh = () => {
        onRefresh();
        setShowConfirmRefresh(false);
    };

    if (!shouldRender) return null;

    return (
        <div className={`${styles.chatWidget} ${isClosing ? styles.closing : ''}`}>
            <div className={styles.chatContainer} ref={chatContainerRef}>
                <ChatHeader onClose={onClose} onRefresh={handleRefreshClick} />

                {showFeedback && (
                    <ChatFeedback
                        caseId={caseId}
                        feedbackSent={feedbackSent}
                        feedbackLiked={feedbackLiked}
                        onSendFeedback={onSendFeedback}
                    />
                )}

                <ChatMessages messages={messages} isTyping={isTyping} user={user} onClose={onClose} onRefresh={handleRefreshClick} disabled={disabled || isSendingContext} hasError={hasError} />
                <ChatInput onSendMessage={onSendMessage} disabled={disabled || isSendingContext} />

                {showConfirmRefresh && (
                    <div className={styles.confirmOverlay}>
                        <div className={styles.confirmBox}>
                            <p className={styles.confirmText}>¿Seguro que quieres limpiar el chat?</p>
                            <div className={styles.confirmActions}>
                                <button className={styles.confirmCancel} onClick={() => setShowConfirmRefresh(false)}>Cancelar</button>
                                <button className={styles.confirmAccept} onClick={handleConfirmRefresh}>Limpiar</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatWidget;
